import type { JSX, ReactNode } from 'react';
import { Box, chakra, Link, Text } from '@chakra-ui/react';

import type { ApiNotification } from '@izimi/api';
import { ApiNotificationType } from '@izimi/api';
import { buildQueryString } from '@izimi/utils';

import { CitizenDocumentsReceivedActions } from './CitizenDocumentsReceivedActions';
import { CommunicationReceivedActions } from './CommunicationReceivedActions';
import { ContactRequestReceivedActions } from './ContactRequestReceivedActions';
import { DocumentsReceivedActions } from './DocumentsReceivedActions';

const Span = chakra('span');

type NotificationActionItem = {
  [key in ApiNotificationType]?: {
    actions?: (notification: ApiNotification & { type: key }) => ReactNode;
    messageComponents?: <Routes extends Record<string, string>>(
      notification: ApiNotification & { type: key },
      routes: Routes,
    ) => JSX.Element[];
  };
};

export const notificationActionItems: NotificationActionItem = {
  [ApiNotificationType.CommunicationReceived]: {
    actions: (notification) => (
      <>
        {notification.data.title && notification.data.title.length > 0 ? (
          <Box p={4} bgColor='primary100' borderRadius='md' mt={4} mb={4}>
            <Text wordBreak='break-word'>{notification.data.title}</Text>
          </Box>
        ) : null}
        <CommunicationReceivedActions notification={notification} />
      </>
    ),
  },
  [ApiNotificationType.StructuredCommunicationReceived]: {
    actions: (notification) => <CommunicationReceivedActions notification={notification} />,
  },
  [ApiNotificationType.SharedDocumentsReceived]: {
    actions: (notification) => (
      <>
        {notification.data.message && notification.data.message.length > 0 ? (
          <Box p={4} bgColor='primary100' borderRadius='md' mt={4} mb={4}>
            <Text wordBreak='break-word'>{notification.data.message}</Text>
          </Box>
        ) : null}
        <CitizenDocumentsReceivedActions notification={notification} navigateTo='/sharing/to-you' />
      </>
    ),
  },
  [ApiNotificationType.PushedDocumentsReceived]: {
    actions: (notification) => (
      <>
        {notification.data.message && notification.data.message.length > 0 ? (
          <Box p={4} bgColor='primary100' borderRadius='md' mt={4} mb={4}>
            <Text wordBreak='break-word'>{notification.data.message}</Text>
          </Box>
        ) : null}
        <CitizenDocumentsReceivedActions notification={notification} navigateTo='/sharing/to-you' />
      </>
    ),
  },
  [ApiNotificationType.ContactRequestReceived]: {
    actions: (notification) => <ContactRequestReceivedActions notification={notification} />,
  },
  [ApiNotificationType.NotaryDocumentsReceived]: {
    messageComponents: (notification, routes) => [
      <Span key='notary-docs-sender' fontWeight='600' />,
      <Link
        key='notary-docs-link'
        href={`${routes.VaultPending}${buildQueryString({
          documentNames: notification.data.documentNames,
          documentIds: notification.data.documentIds,
          message: notification.data.message,
          notificationId: notification.id,
        })}`}
        fontWeight='600'
        textDecoration='underline'
      />,
      <Span key='notary-docs-count' fontWeight='600' />,
    ],
    actions: (notification) => (
      <>
        {notification.data.message && notification.data.message.length > 0 ? (
          <Box p={4} bgColor='primary100' borderRadius='md' mt={4} mb={4}>
            <Text wordBreak='break-word'>{notification.data.message}</Text>
          </Box>
        ) : null}
        <DocumentsReceivedActions notification={notification} />
      </>
    ),
  },
  [ApiNotificationType.StudyPushedDocumentsReceived]: {
    messageComponents: (notification, routes) => [
      <Span key='study-docs-sender' fontWeight='600' />,
      <Link
        key='study-docs-link'
        href={`${routes.VaultPending}${buildQueryString({
          documentNames: notification.data.documentNames,
          documentIds: notification.data.documentIds,
          message: notification.data.message,
          notificationId: notification.id,
        })}`}
        fontWeight='600'
        textDecoration='underline'
      />,
      <Span key='study-docs-count' fontWeight='600' />,
    ],
    actions: (notification) => (
      <>
        {notification.data.message && notification.data.message.length > 0 ? (
          <Box p={4} bgColor='primary100' borderRadius='md' mt={4} mb={4}>
            <Text wordBreak='break-word'>{notification.data.message}</Text>
          </Box>
        ) : null}
        <DocumentsReceivedActions notification={notification} />
      </>
    ),
  },
  [ApiNotificationType.NotaryAutomaticallyClaimedVault]: {
    messageComponents: (notification, routes) => [
      <Link
        key='claimed-vault-link'
        href={`${routes.VaultTransfer}${buildQueryString({
          nrn: notification.data.nrn,
        })}`}
        fontWeight='600'
        textDecoration='underline'
      />,
    ],
  },
} as const;
